import React, { Dispatch, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { selectConversation } from '@reducers/data/nexus';
import { ConversationMessage } from '@models/nexus';
import { ID } from '@app/types';
import Pill from '@components/Pill';
import styles from './Conversation.module.scss';
import {
  ActionCopyIcon,
  ChevronRightIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  XIcon,
} from '@components/Icons';
import { fetchContact, selectContact } from '@actions/contacts';
import { alertUser } from '@actions/alerts';
import ConversationStarterSuggestions from '../ConversationStarterSuggestions/ConversationStarterSuggestions';
import { clearNewConversation } from '@actions/nexus';
import { useLocation, useNavigate } from 'react-router-dom';
import { ContactCard } from '../ConversationStarterSuggestions/ContactCard/ContactCard';

const UserMessage = ({ text }: { text: string }) => (
  <div className={cn(styles.message, styles.userMessage)}>{text}</div>
);

const RelevantContactPill = ({ contactID }: { contactID: ID }) => {
  const dispatch = useDispatch();
  const contact = useSelector(selectContact(contactID));
  const { isFetching, isPersisted } = contact;

  useEffect(() => {
    if (!isPersisted) {
      dispatch(fetchContact({ id: contactID }));
    }
  }, []);

  return !isFetching && <Pill contact={contact} clickable />;
};

const RelevantContacts = ({ contactIDs }: { contactIDs: ID[] }) => {
  return (
    <div className={styles.relevantContacts}>
      {contactIDs.map((contactID) => (
        <RelevantContactPill key={contactID} contactID={contactID} />
      ))}
    </div>
  );
};

// const LoadingBotMessage = ({ text }: { text: string }) => (
//   <>
//     {text.split('\n').map((block, blockIdx) => (
//       <div key={blockIdx}>
//         {block.split(' ').map((word, i) => (
//           <span className={styles.loadingWord} key={i}>
//             {word}{' '}
//           </span>
//         ))}
//       </div>
//     ))}
//   </>
// );

const BotMessage = ({
  text,
  relevantContacts,
  terminated,
  error,
  paywall,
}: Partial<ConversationMessage> & { paywall?: boolean }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const submitFeedback = () => dispatch(alertUser('Thanks for the feedback!', 'success'));
  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(text)
      .then(() => dispatch(alertUser('Copied to clipboard', 'success')));
  };

  return (
    <div
      className={cn(styles.message, styles.botMessage, (error || paywall) && styles.messageError)}
    >
      {error ? (
        <div>
          {text}{' '}
          <span
            className={cn(styles.startOverBtn)}
            onClick={() => {
              dispatch(clearNewConversation);
              if (location.pathname !== '/nexus') {
                navigate('/nexus');
              }
            }}
          >
            {' '}
            start a new conversation
            <ChevronRightIcon width="11" height="11" color="textSecondary" />
          </span>
        </div>
      ) : (
        <ReactMarkdown
          key={terminated ? 'terminated' : 'streaming'}
          children={text}
          linkTarget="_blank"
        />
      )}
      {!error && !paywall && (
        <div className={cn(styles.messageUtilityBar, terminated && styles.messageTerminated)}>
          <RelevantContacts contactIDs={relevantContacts} />
          <div className={styles.messageActions}>
            <ActionCopyIcon
              width="16"
              height="16"
              color="textSecondary"
              hoverOpacity={false}
              hover
              onClick={copyToClipboard}
            />
            <ThumbsUpIcon onClick={submitFeedback} />
            <ThumbsDownIcon onClick={submitFeedback} />
          </div>
        </div>
      )}
    </div>
  );
};

export default function ChatConversation({
  conversationID = 'new',
  setConversationTargetContactID,
}: {
  conversationID: ID;
  setConversationTargetContactID?: Dispatch<ID>;
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const conversation = useSelector(selectConversation(conversationID));
  const conversationTargetContact = useSelector(selectContact(conversation.targetContactID));
  const messages = conversation.messages
    .valueSeq()
    .toArray()
    .filter((message) => !!message.text);

  const renderMessage = (message: ConversationMessage) => {
    if (!message.text) {
      return null;
    }

    if (message.fromUser) {
      return <UserMessage key={message.id} text={message.text} />;
    } else {
      return (
        <BotMessage
          key={message.id}
          text={message.text}
          relevantContacts={conversation.includeTargetContactID(message.relevantContacts)}
          terminated={message.terminated}
          error={message.error}
        />
      );
    }
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <>
      {messages.length === 0 ? (
        <ConversationStarterSuggestions setTargetContactID={setConversationTargetContactID} />
      ) : (
        <div className={styles.conversation} ref={containerRef}>
          {conversation.targetContactID && (
            <div className={styles.conversationContactCard}>
              <ContactCard contact={conversationTargetContact} detailType="lastInteraction" />
              <XIcon
                color="textSecondary"
                className={styles.closeIcon}
                onClick={() => {
                  dispatch(clearNewConversation);
                  setConversationTargetContactID(null);
                  if (location.pathname !== '/nexus') {
                    navigate('/nexus');
                  }
                }}
                width={13}
                height={13}
                hover
              />
            </div>
          )}

          {messages.map(renderMessage)}

          {conversation.nexusDisabled && (
            <BotMessage
              text="Hi! You need to enable Nexus to get started. Please do so using the button below."
              paywall
            />
          )}
        </div>
      )}
    </>
  );
}
